
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
/* eslint-disable @typescript-eslint/no-empty-interface */

import { ReactElement, useState, useRef } from 'react';
import { AiOutlineStar, AiOutlineArrowRight } from 'react-icons/ai';
import { RiMoneyDollarCircleLine } from 'react-icons/ri';
import { SubmitHandler, FieldErrors } from 'react-hook-form';
import useTranslation from 'next-translate/useTranslation';
import { GetStaticProps } from 'next';

import { TRANSLATE_NAMESPACES } from 'lib/constants/translateNamespaces';
import { candidateColorScheme } from 'lib/settings/theme/colors/candidate';
import CreateFreeProfile from 'components/templates/createFreeProfile';
import { InputsFirstStep } from 'lib/schemas/candidates/firstStepRegistration';
import { InputsSecondStep } from 'lib/schemas/candidates/secondStepRegistration';
import useSession from 'lib/hooks/useSession';
import { useMarketingParams } from 'lib/hooks/useMarketingParams';
import { AUTH_CANDIDATE_PATH } from 'lib/constants/auth';
import {
  useCheckCandidateIsRegisteredQuery,
  useRegisterCandidateFirstStepMutation,
} from 'lib/generated/graphql';
import { ThemeProvider } from 'components/atoms/themeProvider';
import { NextSeo } from 'components/atoms/nextSeo';

const CreateFreeProfilePage = (): ReactElement => {
  const [step, setStep] = useState(1);
  const [candidateFirstStepData, setCandidateFirstStepData] = useState({
    email: '',
    acceptEmailNotifications: true,
  });
  const [formErrors, setFormErrors] = useState<FieldErrors>([]);
  const formRef = useRef<HTMLDivElement>(null);

  const { createSession } = useSession();
  const { refetch } = useCheckCandidateIsRegisteredQuery({ skip: true });
  const [registerCandidateFirstStepMutation] =
    useRegisterCandidateFirstStepMutation();
  const {
    utmSource,
    utmMedium,
    utmTerm,
    utmCampaign,
    fromReferral,
    firstReferer,
    deviceType,
    slugPosition,
  } = useMarketingParams();
  const lastRenderedJob = slugPosition;

  const onSubmitFirstStep: SubmitHandler<InputsFirstStep> = async ({
    email,
    acceptEmailNotifications,
  }) => {
    try {
      const res = await refetch({ email });
      if (res.data.checkCandidateIsRegistered.isRegistered) {
        setFormErrors([
          { type: 'email', message: 'Email já em uso na plataforma.' },
        ]);
      } else {
        setCandidateFirstStepData({ email, acceptEmailNotifications });
        setStep(2);
      }
    } catch (_) {
      setFormErrors([
        { type: 'email', message: 'Falha ao comunicar-se com o sistema.' },
      ]);
    }
  };

  const onSubmitSecondStep: SubmitHandler<InputsSecondStep> = async (data) => {
    const res = await registerCandidateFirstStepMutation({
      variables: {
        email: candidateFirstStepData.email,
        name: data.fullName,
        password: data.password,
        emailNotification: candidateFirstStepData.acceptEmailNotifications,
        termsOfService: true,
        utmSource: utmSource || 'direct',
        utmMedium: utmMedium || '(none)',
        utmTerm,
        utmCampaign: utmCampaign || '',
        fromReferral,
        firstReferer,
        deviceType,
        lastRenderedSlug: lastRenderedJob,
      },
    });

    if (res.data?.registerCandidateFirstStep.errors.length === 0) {
      const token = res.data?.registerCandidateFirstStep.authToken;
      createSession(token, 'candidate', AUTH_CANDIDATE_PATH(token));
    } else {
      setFormErrors([
        {
          type: 'repeatPassword',
          message: res.data?.registerCandidateFirstStep.errors[0],
        },
      ]);
    }
  };

  const { t } = useTranslation(TRANSLATE_NAMESPACES.createProfile);

  return (
    <ThemeProvider colors={candidateColorScheme}>
      <NextSeo
        title='GeekHunter: Criar Perfil Grátis'
        description='Dev, receba a proposta de emprego dos seus sonhos!'
        canonical='https://www.geekhunter.com.br/criar-perfil-gratis'
      />

      <CreateFreeProfile
        /**
         * Create account form Props
         */
        formTitle={t('formTitle')}
        formList={[
          {
            icon: RiMoneyDollarCircleLine,
            text: 'Saiba antes sobre remuneração e benefícios das vagas',
          },
          {
            icon: AiOutlineStar,
            text: 'Com um único cadastro você participa de todos os processos!',
          },
        ]}
        formErrors={formErrors}
        submitFirstStep={onSubmitFirstStep}
        submitSecondStep={onSubmitSecondStep}
        formStep={step}
        changeFormStep={setStep}
        formRef={formRef}
        /**
         * Registration steps props
         */
        registrationStepsTitle={
          <>
            Cadastrar na GeekHunter <br /> é simples 👉
          </>
        }
        registrationSteps={[
          {
            step: '01',
            title: 'Crie o seu perfil e deixe que as empresas encontrem você',
            subTitle: '',
            text: 'Importe seu perfil do LinkedIn e conte-nos sobre seu momento profissional, sua remuneração pretendida e seus interesses',
          },
          {
            step: '02',
            title:
              'Demonstre interesse, receba convites e escolha a melhor proposta',
            subTitle: '',
            text: 'Logo após a aprovação do seu perfil, você já está apto a receber convites de entrevista. Demonstre interesse nas vagas e aumente suas chances',
          },
          {
            step: '03',
            title:
              'Mantenha seu perfil atualizado e seja encontrado quando quiser',
            subTitle: '',
            text: 'Controle quando estará visível para as empresas: mantenha seu momento profissional, experiências e remuneração pretendida em dia',
          },
        ]}
        /**
         * Companies using geek and geek's feedback props
         */
        companiesTitle='As empresas que contratam nossos talentos de tecnologia'
        companiesLogo={[
          { src: '/companies/amazon.png', alt: 'Amazon', w: [140], h: [40] },
          {
            src: '/companies/centauro.png',
            alt: 'Centauro',
            w: [140],
            h: [30],
          },
          {
            src: '/companies/clickbus-white.png',
            alt: 'Click Bus',
            w: [140],
            h: [40],
          },
          { src: '/companies/esss-white.png', alt: 'Esss', w: [140], h: [40] },
          {
            src: '/companies/guiabolso.png',
            alt: 'Guiabolso',
            w: [160],
            h: [40],
          },
          {
            src: '/companies/meetime-white.png',
            alt: 'MeeTime',
            w: [140],
            h: [40],
          },
          {
            src: '/companies/mercadobitcoin.png',
            alt: 'Mercado Bitcoin',
            w: [140],
            h: [50],
          },
          {
            src: '/companies/rankmyapp.png',
            alt: 'Rank My App',
            w: [140],
            h: [50],
          },
          {
            src: '/companies/resultadosdigitais.png',
            alt: 'Resultados Digitais',
            w: [140],
            h: [40],
          },
          { src: '/companies/zup.png', alt: 'Zup', w: [130], h: [50] },
        ]}
        geekiesTitle='Por que profissionais amam a Geek? 💜'
        geekiesComments={[
          {
            image: {
              src: '/geeks/geek1.webp',
              alt: 'Guilherme Assemany',
            },
            name: 'Guilherme Assemany',
            position: 'IT Manager',
            comment:
              'A GeekHunter me surpreendeu! A plataforma é bastante eficaz! Vale também falar da atenção dos funcionários. Todos que falaram comigo me trataram super bem e sempre acompanharam todo o processo de seleção, tirando dúvidas e ajudando no que fosse necessário. Recomendo muito!',
          },
          {
            image: {
              src: '/geeks/geek2.webp',
              alt: 'Daniela Cruz Tirado',
            },
            name: 'Daniela Cruz Tirado',
            position: 'Software Engineer',
            comment:
              'Quando me cadastrei na GeekHunter já comecei gostando muito da plataforma. Ela tem tudo que um profissional da área de TI precisa mostrar para as empresas. Achei muito legal os feedbacks que enviam por email te atualizando sobre os processos seletivos e as dicas que dão para melhorar o perfil e ser mais visto entre as empresas.',
          },
          {
            image: {
              src: '/geeks/geek3.webp',
              alt: 'Fabio Gunkel',
            },
            name: 'Fabio Gunkel',
            position: 'IT Manager',
            comment:
              'Me interessei pelo modelo de contratação e me cadastrei. As provas foram simples e diretas, mas sempre cobrando algum conteúdo chave. Depois de aprovado não demorou uma semana até a primeira entrevista. Adorei o lugar e estou nele até hoje. Recomendo e muito este serviço para todos os meus amigos da área.',
          },
        ]}
        geekCommentsButton='Criar meu perfil agora'
        geekCommentsButtonIcon={AiOutlineArrowRight}
        /**
         * Free platform for devs session props
         */
        freePlatformBadge='Custa R$0 😱'
        freePlatformTitle={
          <>
            Sim, uma plataforma <br /> 100% gratuita para <br /> profissionais.
          </>
        }
        freePlatformButton='Criar meu perfil agora'
        freePlatformButtonIcon={AiOutlineArrowRight}
      />
    </ThemeProvider>
  );
};

// getStaticProps is required, so nextTranslate will be able to generate the translated string on build time
 const _getStaticProps: GetStaticProps = () => ({
  props: {},
});

export default CreateFreeProfilePage;


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        let res = _getStaticProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/criar-perfil-gratis/index',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  